import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import Dashboard from './views/Dashboard/components/Dashboard';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppContextProvider from './contexts/AppContextProvider';
import { createContext, ReactNode, useEffect, useState } from 'react';
import firebaseService from './firebase/firebaseService';
import firebase from 'firebase/compat/app';
import ProfileEditable from './views/Profile/components/EditProfile';
import MainLayout from './MainLayout';
import Profile from './views/Profile/components/Profile';
import CreateKlydo from './views/Klydos/components/CreateKlydo';
import Klydos from './views/Klydos/KlydosList/components/Klydos';
import PublicKlydoView from './views/PublicKlydo/components/PublicKlydoView';
import * as Sentry from '@sentry/react';
import Collections from './views/Collections/components/Collections';

const queryClient = new QueryClient();

interface AuthContextType {
  user: firebase.User | null;
  setUser: React.Dispatch<React.SetStateAction<firebase.User | null>>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const RoutesWrapper = ({ children }: { children: ReactNode }) => {
  const environment =
    !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
      ? 'development'
      : 'production';
  if (environment === 'production') {
    return <SentryRoutes>{children}</SentryRoutes>;
  } else {
    return <Routes>{children}</Routes>;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<firebase.User | null>(null);

  useEffect(() => {
    const unsubscribe: firebase.Unsubscribe =
      firebaseService.onAuthStateChanged((authUser: firebase.User | null) => {
        setUser(authUser);
      });
    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <RoutesWrapper>
          {/* Public routes - outside of auth */}
          <Route path="/public/klydo/:klydoId" element={<PublicKlydoView />} />

          {/* Protected routes - wrapped with auth */}
          <Route
            element={
              <AppContextProvider>
                <MainLayout />
              </AppContextProvider>
            }
          >
            <Route path="/profile" element={<Profile />} />
            <Route path="/edit-profile" element={<ProfileEditable />} />
            <Route path="/klydo/:klydoId" element={<CreateKlydo />} />
            <Route path={'/dashboard'} element={<Dashboard />} />
            <Route path={'/klydos'} element={<Klydos />} />
            <Route path={'/collections'} element={<Collections />} />
            <Route path="*" element={<Navigate replace to={'/dashboard'} />} />
          </Route>
        </RoutesWrapper>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
export default App;
